import styled from '@emotion/styled';
import { Select } from 'antd';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateInput } from 'semantic-ui-calendar-react';

import closedEyeIcon from 'src/public/img/eye-closed.svg';
import openEyeIcon from 'src/public/img/eye-open.svg';
import { color, font, shadow } from 'src/shared/styles/variables';

const DatePicker = styled<any>(DateInput)({
    '&&.field .ui.icon.input': {
        width: '100%',
    },
    '&&.field input': {
        cursor: 'pointer',
        padding: 11,
        paddingTop: 16,
        color: color.blueBayoux,
        border: `2px solid ${color.linkWaterLight}`,
        boxShadow: shadow.regularShadow,
        ...font.medium(font.size.medium),
    },
});

interface InputFieldProps {
    invalid: boolean;
}

const InputField = styled.input<InputFieldProps>(
    props => ({
        width: '100%',
        borderRadius: 5,
        padding: 10,
        border: `1px solid ${props.invalid ? color.brinkPink : color.linkWaterLight} !important`,
        boxShadow: ` ${shadow.regularShadow} !important`,
        color: `${color.fiord} !important`,
        '::placeholder': {
            color: color.casper,
        },
    }),
    font.heavy(font.size.base),
);

const TextField = styled.textarea<InputFieldProps>(
    props => ({
        width: '100%',
        height: 150,
        borderRadius: 5,
        padding: 10,
        border: `1px solid ${props.invalid ? color.brinkPink : color.linkWaterLight} !important `,
        boxShadow: `${shadow.regularShadow} !important `,
        color: color.fiord,
        '::placeholder': {
            color: color.baliHai,
            ...font.light(font.size.base),
        },
    }),
    font.heavy(font.size.base),
);

const InputLabel = styled.label(
    {
        display: 'block',
        marginTop: 20,
        marginBottom: 8,
        color: `${color.steelGray} !important`,
    },
    font.heavy(font.size.base),
);

const AsteriskLabel = styled.span({
    color: color.wildWatermelon,
});

const StyledSelect = styled(Select)({
    width: '100%',
    boxShadow: shadow.lightShadow,
    '&& ul': {
        paddingLeft: 0,
    },
    '&& li:not(:last-child)': {
        marginBottom: 0,
    },
});

interface ErrorMessageProps {
    visible: boolean;
}

const ErrorMessage = styled.span<ErrorMessageProps>(
    ({ visible }) => ({
        display: 'block',
        marginTop: visible ? 8 : 0,
        color: color.wildWatermelon,
        transition: '.2s ease-in-out margin-top',
    }),
    font.book(font.size.base),
);

const PasswordInputWrapper = styled.div({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
});

const EyeIcon = styled.img({
    position: 'absolute',
    right: 0,
    padding: 15,
    cursor: 'pointer',
});

interface PasswordInputProps {
    fieldId: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: (e: React.FocusEvent) => void;
    error?: string;
    testID?: string;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
    fieldId,
    value,
    onChange,
    onBlur = () => null,
    error,
    testID,
}) => {
    const [passwordVisible, setPasswordVisible] = useState(false);

    return (
        <PasswordInputWrapper>
            <InputField
                type={passwordVisible ? 'text' : 'password'}
                id={fieldId}
                name={fieldId}
                value={value}
                invalid={!!error}
                onBlur={onBlur}
                onChange={e => onChange(e.target.value)}
                data-testid={testID}
            />
            <EyeIcon
                src={passwordVisible ? openEyeIcon : closedEyeIcon}
                onClick={() => setPasswordVisible(!passwordVisible)}
            />
        </PasswordInputWrapper>
    );
};

interface Props extends PasswordInputProps {
    type: string;
    label?: string;
    placeholder?: string;
    readOnly?: boolean;
    showAsterisk?: boolean;
    errorMessageTestID?: string;
    options?: { text: string; value: string }[];
    onChange: (value: any) => void;
}

const minDate = moment().subtract(90, 'years');
const maxDate = moment().subtract(10, 'years');

export const Input: React.FC<Props> = ({
    fieldId,
    label,
    type,
    value,
    onChange,
    onBlur,
    placeholder = '',
    error = '',
    testID = '',
    readOnly,
    options = [],
    errorMessageTestID,
    showAsterisk,
}) => {
    const { t } = useTranslation();
    const handleDateChange = (_e, data: { value: string }) => onChange(data.value);

    return (
        <>
            {label && (
                <InputLabel htmlFor={fieldId}>
                    {label}
                    {showAsterisk ? <AsteriskLabel> *</AsteriskLabel> : null}
                </InputLabel>
            )}

            {type == 'date' ? (
                <DatePicker
                    id={fieldId}
                    name="date"
                    startMode="year"
                    dateFormat="YYYY-MM-DD"
                    placeholder={t('inputs.dateTime.dateOfBirthFormat')}
                    initialDate="1990-01-01"
                    minDate={minDate}
                    maxDate={maxDate}
                    value={value}
                    onChange={handleDateChange}
                    autoComplete="off"
                    closable
                    data-testid={testID}
                />
            ) : type == 'password' ? (
                <PasswordInput
                    fieldId={fieldId}
                    value={value}
                    onChange={onChange}
                    error={error}
                    onBlur={onBlur}
                    testID={testID}
                />
            ) : type == 'textarea' ? (
                <TextField
                    id={fieldId}
                    name={fieldId}
                    value={value}
                    placeholder={placeholder}
                    invalid={!!error}
                    onChange={e => onChange(e.target.value)}
                    onBlur={onBlur}
                    data-testid={testID}
                />
            ) : type == 'dropdown' ? (
                <StyledSelect
                    size="large"
                    placeholder={placeholder}
                    value={value}
                    onChange={(option: any) => onChange(option)}
                    mode="multiple"
                    data-testid={testID}
                >
                    {options.map(option => (
                        <Select.Option key={option.text} value={option.value}>
                            {option.text}
                        </Select.Option>
                    ))}
                </StyledSelect>
            ) : type == 'single-select-dropdown' ? (
                <StyledSelect
                    size="large"
                    placeholder={placeholder}
                    value={value}
                    onChange={(option: any) => onChange([option])}
                    data-testid={testID}
                >
                    {options.map(option => (
                        <Select.Option key={option.text} value={option.value}>
                            {option.text}
                        </Select.Option>
                    ))}
                </StyledSelect>
            ) : (
                <InputField
                    type={type}
                    id={fieldId}
                    name={fieldId}
                    value={value}
                    placeholder={placeholder}
                    invalid={!!error}
                    onChange={e => onChange(e.target.value)}
                    onBlur={onBlur}
                    readOnly={readOnly}
                    data-testid={testID}
                />
            )}

            <ErrorMessage visible={!!error} data-testid={errorMessageTestID}>
                {error}
            </ErrorMessage>
        </>
    );
};
